/**
 * Parent medicine history — every recorded dose in one table, newest first.
 *
 * This is the flat counterpart to the weekly grid on /plan: the grid answers
 * "how did this week go", this answers "when exactly was this medicine given".
 * Filters narrow by medicine, status and date range; the CSV export hands the
 * doctor exactly the rows on screen. Clicking a row opens that day in the day
 * view (/?date=), which is where a dose can be corrected.
 *
 * Read-only by design — nothing here changes a prescription or a dose.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ADMINISTRATION_STATUSES, dayBounds, toCalendarDate } from '@toxofollow/shared';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Badge, Button, Card, Field, Table, inputClass } from '../../components/ui.js';
import { QueryState, Select } from '../../components/form.js';

interface HistoryRow {
  id: string;
  orderId: string;
  scheduledDate: string;
  scheduledTime: string | null;
  status: string;
  actualDate: string | null;
  actualTime: string | null;
  doseGiven: string | null;
  doseUnit: string | null;
  note: string | null;
  concept: string;
  brandName: string | null;
  localName: string | null;
}
interface HistoryResp { from: string; to: string; total: number; administrations: HistoryRow[] }
interface Order { id: string; concept: string; brandName: string | null }
interface PatientResp { patient: { treatmentStartDate: string | null } | null }

function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'info' {
  if (status === 'taken') return 'ok';
  if (status === 'late' || status === 'partial') return 'warn';
  if (status === 'missed' || status === 'refused' || status === 'vomited_after_dose') return 'danger';
  return 'info';
}

function csvCell(v: string | null | undefined): string {
  const s = v ?? '';
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function ParentHistory() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const patientQ = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResp>('/patient') });
  const today = toCalendarDate(new Date());
  const treatmentStart = patientQ.data?.patient?.treatmentStartDate ?? null;
  const bounds = useMemo(() => dayBounds(treatmentStart, today), [treatmentStart, today]);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [orderId, setOrderId] = useState('');
  const [status, setStatus] = useState('');

  const effFrom = from || bounds.min;
  const effTo = to || bounds.max;

  const ordersQ = useQuery({ queryKey: ['medOrders'], queryFn: () => api.get<{ orders: Order[] }>('/medications/orders') });

  const qs = new URLSearchParams({ from: effFrom, to: effTo });
  if (orderId) qs.set('orderId', orderId);
  if (status) qs.set('status', status);

  const q = useQuery({
    queryKey: ['administrations', effFrom, effTo, orderId, status],
    queryFn: () => api.get<HistoryResp>(`/medications/administrations?${qs.toString()}`),
  });

  const rows = q.data?.administrations ?? [];
  const medName = (r: HistoryRow) => r.brandName || r.localName || t(`concept.${r.concept}`);

  const columns = [
    t('history.colDate'), t('history.colScheduled'), t('history.colGiven'),
    t('history.colMedicine'), t('history.colDose'), t('history.colStatus'), t('history.colNote'),
  ];

  const exportCsv = () => {
    const body = rows.map((r) => [
      r.scheduledDate,
      r.scheduledTime ?? '',
      r.actualTime ? `${r.actualDate ?? r.scheduledDate} ${r.actualTime}` : '',
      medName(r),
      r.doseGiven ? `${r.doseGiven} ${r.doseUnit ?? ''}`.trim() : '',
      t(`admin.${r.status}`),
      r.note ?? '',
    ]);
    const csv = [columns, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    // A BOM so Excel opens the accented French labels correctly.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `toxofollow-doses-${effFrom}_${effTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">{t('history.title')}</h1>
        <p className="text-sm text-slate-500">{t('history.subtitle')}</p>
      </div>

      <Card>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('history.filterMed')}>
            <Select value={orderId} onChange={(e) => setOrderId(e.target.value)}>
              <option value="">{t('history.allMeds')}</option>
              {(ordersQ.data?.orders ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.brandName || t(`concept.${o.concept}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('history.filterStatus')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('history.allStatuses')}</option>
              {ADMINISTRATION_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`admin.${s}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('history.from')}>
            <input type="date" className={inputClass} value={effFrom} min={bounds.min} max={bounds.max} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('history.to')}>
            <input type="date" className={inputClass} value={effTo} min={bounds.min} max={bounds.max} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-slate-500">{t('history.count', { n: q.data?.total ?? 0 })}</span>
          <Button variant="secondary" onClick={exportCsv} disabled={rows.length === 0}>{t('history.export')}</Button>
        </div>
      </Card>

      <Card>
        <QueryState
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          isEmpty={rows.length === 0}
          emptyMessage={t('history.empty')}
          errorFallback={t('common.error')}
        >
          <Table headers={columns}>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/?date=${r.scheduledDate}`)}
                className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50"
              >
                <td className="py-2 pr-4 whitespace-nowrap">{r.scheduledDate}</td>
                <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{r.scheduledTime ?? '—'}</td>
                <td className="py-2 pr-4 whitespace-nowrap">{r.actualTime ?? '—'}</td>
                <td className="py-2 pr-4">{medName(r)}</td>
                <td className="py-2 pr-4 whitespace-nowrap">{r.doseGiven ? `${r.doseGiven} ${r.doseUnit ?? ''}` : '—'}</td>
                <td className="py-2 pr-4"><Badge tone={statusTone(r.status)}>{t(`admin.${r.status}`)}</Badge></td>
                <td className="py-2 pr-4 text-slate-500">{r.note ?? ''}</td>
              </tr>
            ))}
          </Table>
        </QueryState>
      </Card>
    </div>
  );
}
